import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const COLORS = ["#22C55E", "#3B82F6", "#F59E0B", "#EF4444", "#A855F7", "#EC4899", "#14B8A6"];

export default function Accounts() {
  const { data: accounts } = useSWR("/accounts", fetcher);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("bank");
  const [balance, setBalance] = useState("");
  const [color, setColor] = useState(COLORS[0]);

  const create = async () => {
    if (!name.trim()) return toast.error("Nama akun harus diisi");
    try {
      await api.post("/accounts", {
        name: name.trim(),
        type,
        balance: Number(balance) || 0,
        color,
      });
      toast.success("Akun ditambahkan");
      mutate("/accounts"); mutate("/reports/summary");
      setOpen(false); setName(""); setBalance(""); setType("bank"); setColor(COLORS[0]);
    } catch { toast.error("Gagal menambahkan akun"); }
  };

  const del = async (id) => {
    if (!confirm("Hapus akun ini beserta semua transaksinya?")) return;
    try {
      await api.delete(`/accounts/${id}`);
      toast.success("Akun dihapus");
      mutate("/accounts"); mutate("/transactions"); mutate("/reports/summary");
    } catch { toast.error("Gagal menghapus"); }
  };

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4">
        <div>
          <p className="label-caps">Wallet</p>
          <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">Accounts</h1>
          <p className="text-sm text-muted-foreground mt-2">Kelola rekening bank, e-wallet, kartu kredit, dan tabungan.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-foreground text-background hover:bg-foreground/90 h-11" data-testid="add-account-btn">
              <Plus className="h-4 w-4 mr-2" /> Tambah Akun
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Akun Baru</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Nama Akun</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-11" placeholder="BCA, Gopay, dsb" data-testid="account-name-input" />
              </div>
              <div>
                <Label>Tipe</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="mt-2 h-11" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="ewallet">E-Wallet</SelectItem>
                    <SelectItem value="credit">Kartu Kredit</SelectItem>
                    <SelectItem value="savings">Tabungan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Saldo Awal (IDR)</Label>
                <Input type="number" value={balance} onChange={(e) => setBalance(e.target.value)} className="mt-2 h-11 font-mono" placeholder="0" data-testid="account-balance-input" />
              </div>
              <div>
                <Label>Warna</Label>
                <div className="flex gap-2 mt-2">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`h-8 w-8 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-offset-background ring-foreground scale-110" : ""}`}
                      style={{ background: c }}
                      data-testid={`color-${c}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="bg-foreground text-background hover:bg-foreground/90" data-testid="create-account-submit">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {(accounts || []).map((a) => (
          <div key={a.id} className="border border-border bg-card p-5 relative overflow-hidden group" data-testid={`account-card-${a.id}`}>
            <div className="h-1 absolute top-0 left-0 right-0" style={{ background: a.color }} />
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{a.type}</p>
                <p className="font-heading text-xl mt-1">{a.name}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(a.id)} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`account-delete-${a.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <p className="metric-num text-3xl mt-6">{formatIDR(a.balance)}</p>
          </div>
        ))}
        {(!accounts || accounts.length === 0) && (
          <div className="col-span-full border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            Belum ada akun. Klik "Tambah Akun" untuk memulai.
          </div>
        )}
      </div>
    </div>
  );
}
